import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api.js";

// /admin, protected by Cloudflare Access. List + create + post pool.
export default function AdminApp() {
  const [list, setList] = useState(null);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState("participants");

  const load = () => api.adminList().then((r) => setList(r.participants)).catch((e) => setError(e.message));
  useEffect(() => { load(); }, []);

  if (error) return <Shell><p className="qf-lead">{error}</p><p className="muted-sm">This area is gated by Cloudflare Access.</p></Shell>;

  return (
    <Shell>
      <div className="hstack qf-between">
        <h1 className="qf-title">Participants</h1>
        <div className="hstack">
          <button className={`link-btn ${tab === "participants" ? "" : ""}`} onClick={() => setTab("participants")}>List</button>
          <button className="link-btn" onClick={() => setTab("create")}>Create</button>
          <button className="link-btn" onClick={() => setTab("pool")}>Post pool</button>
        </div>
      </div>

      {tab === "create" && <CreateForm onCreated={() => { setTab("participants"); load(); }} />}
      {tab === "pool" && <PostPool />}
      {tab === "participants" && <ParticipantTable list={list} />}
    </Shell>
  );
}

function ParticipantTable({ list }) {
  const [copied, setCopied] = useState(null);
  if (!list) return <p className="muted-sm">Loading…</p>;
  if (!list.length) return <p className="qf-lead">No participants yet. Create the first one.</p>;
  const copy = (text, id) => { navigator.clipboard.writeText(text); setCopied(id); setTimeout(() => setCopied(null), 1500); };

  return (
    <div className="table-wrap qf-card qf-card--flat" style={{ padding: 0 }}>
      <table className="qf-table">
        <thead><tr>
          <th>Name</th><th>Status</th><th>Progress</th><th>Peers</th><th>Last active</th><th>Link</th><th></th>
        </tr></thead>
        <tbody>
          {list.map((p) => (
            <tr key={p.id}>
              <td>
                <Link to={`/admin/participant/${p.id}`}>{p.full_name}</Link>
                <div className="muted-sm">{p.email}{p.sector ? ` · ${p.sector}` : ""}</div>
                {p.stalled && <span className="flag-stalled">stalled</span>}
              </td>
              <td><span className={`status-pill status-${p.status}`}>{p.status.replace("_", " ")}</span></td>
              <td className="qf-num">{p.overall_progress_pct}%</td>
              <td className="qf-num">{p.peer_count}/3</td>
              <td className="muted-sm">{fmt(p.last_active_at || p.created_at)}</td>
              <td>
                {p.magic_link && <button className="link-btn" onClick={() => copy(p.magic_link, p.id)}>{copied === p.id ? "copied ✓" : "copy link"}</button>}
              </td>
              <td><Link className="link-btn" to={`/admin/participant/${p.id}`}>open →</Link></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CreateForm({ onCreated }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [gender, setGender] = useState("they");
  const [sector, setSector] = useState("");
  const [sectors, setSectors] = useState([]);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.adminSectors().then((r) => setSectors(r.sectors)).catch(() => {}); }, []);

  const create = async () => {
    setBusy(true);
    try { setResult(await api.adminCreate({ full_name: name, email, gender, sector: sector || null })); }
    finally { setBusy(false); }
  };

  if (result) return (
    <div className="qf-card qf-stack">
      <div className="qf-label qf-label--accent">Created, send this link personally</div>
      <CopyRow label="Magic link" value={result.magic_link} />
      <CopyRow label="Mirror link (they'll get this in Quest 3)" value={result.peer_link} />
      <div className="hstack">
        <Link className="qf-btn qf-btn--ghost" to={`/admin/participant/${result.id}`}>Tailor before sending →</Link>
        <button className="qf-btn qf-btn--primary" onClick={onCreated}>Back to list</button>
      </div>
    </div>
  );

  return (
    <div className="qf-card qf-stack center-narrow">
      <input className="input" placeholder="Full name" value={name} onChange={(e) => setName(e.target.value)} />
      <input className="input" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} />
      <select className="input" value={gender} onChange={(e) => setGender(e.target.value)}>
        <option value="they">Pronoun: they / their</option>
        <option value="he">Pronoun: he / his</option>
        <option value="she">Pronoun: she / her</option>
      </select>
      <select className="input" value={sector} onChange={(e) => setSector(e.target.value)}>
        <option value="">No sector pack</option>
        {sectors.map((s) => <option key={s} value={s}>{s}</option>)}
      </select>
      <p className="muted-sm">The sector merges its question pack into this participant's questionnaire. No email is sent, you'll copy the link and send it yourself.</p>
      <div><button className="qf-btn qf-btn--primary" onClick={create} disabled={!name || !email || busy}>{busy ? "Creating…" : "Create participant"}</button></div>
    </div>
  );
}

function PostPool() {
  const [posts, setPosts] = useState(null);
  const [body, setBody] = useState("");
  const load = () => api.adminPostpool().then((r) => setPosts(r.posts));
  useEffect(() => { load(); }, []);
  const add = async () => { if (!body.trim()) return; await api.adminSavePost({ body: body.trim(), ord: (posts?.length || 0) + 1 }); setBody(""); load(); };
  const del = async (id) => { await api.adminDeletePost(id); load(); };
  return (
    <div className="qf-stack">
      <p className="muted-sm">The curated, anonymized posts shown in the "sounds like me" step. Shared by all participants.</p>
      <div className="qf-card qf-stack">
        <textarea className="input" placeholder="Paste an anonymized post…" value={body} onChange={(e) => setBody(e.target.value)} />
        <div><button className="qf-btn qf-btn--primary" onClick={add}>Add post</button></div>
      </div>
      {(posts || []).map((p) => (
        <div className="qf-card qf-between hstack" key={p.id}>
          <span style={{ whiteSpace: "pre-wrap" }}>{p.body}</span>
          <button className="link-btn" onClick={() => del(p.id)}>remove</button>
        </div>
      ))}
    </div>
  );
}

function CopyRow({ label, value }) {
  const [copied, setCopied] = useState(false);
  return (
    <div>
      <div className="qf-label">{label}</div>
      <div className="hstack">
        <input className="input" readOnly value={value} onFocus={(e) => e.target.select()} />
        <button className="qf-btn qf-btn--ghost" onClick={() => { navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1500); }}>
          {copied ? "Copied ✓" : "Copy"}
        </button>
      </div>
    </div>
  );
}

const fmt = (ts) => ts ? new Date(ts).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "not set";

function Shell({ children }) {
  return <div className="app-shell qf-section"><div className="qf-wrap qf-stack--lg">{children}</div></div>;
}
